/**
 * What-to-Bring modal — checklist of items attendees should bring to
 * the workshop, grouped by category (Essentials / Recommended) with a
 * trailing logistics note.
 *
 * Ported from:
 *   - `_legacy/wd.html` lines 398-418 — `#bringModal` shell.
 *   - `_legacy/xaes-content.js` lines 520-605 — `renderBringModal()`
 *     which iterates categories, then items, rendering each item as a
 *     checkable row with label + description.
 *   - `_legacy/xaes-content.js` lines 222-246 — WS4 bring categories
 *     and items + footer note.
 *
 * Chrome contract:
 *   - Uses `PanelDialogContent` + the legacy `.modal-shell` markup so
 *     this briefing modal matches the Wave-4 Asset Locker output modals.
 *     Internal checklist (category headers + checkbox rows + note) is
 *     unchanged — only the outer chrome was swapped.
 *
 * Local state tracks checkbox state per item id. No persistence —
 * Phase 4 / 7 owns durable storage.
 */

import { useState } from "react";
import { CheckSquare, Square, X } from "lucide-react";
import { WorkshopLabelPill, workshopLabelColor } from "@/components/tags/WorkshopLabelPill";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { cn } from "@/lib/utils";
import type { BringContent, BringItem } from "./modal-content";

type BringModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: string;
  bring: BringContent;
};

function itemKey(categoryLabel: string, item: BringItem): string {
  return `${categoryLabel}::${item.label}`;
}

export function BringModal({
  open,
  onOpenChange,
  workshopId,
  bring,
}: BringModalProps) {
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  const requiredCount = bring.categories
    .filter((c) => c.tag === "REQUIRED")
    .reduce((sum, c) => sum + c.items.length, 0);
  const optionalCount = bring.categories
    .filter((c) => c.tag === "OPTIONAL")
    .reduce((sum, c) => sum + c.items.length, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="w-[90vw] max-w-[820px] border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none" }}
      >
        <DialogTitle className="sr-only">
          What to Bring — {workshopId}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Packing checklist for {workshopId}. {requiredCount} required,{" "}
          {optionalCount} optional items. Checked state is local to this
          session.
        </DialogDescription>
        <div
          className="modal-shell"
          style={{ width: "100%", maxHeight: "85vh" }}
        >
          {/* HEADER */}
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CheckSquare
                aria-hidden
                style={{ width: 16, height: 16, color: "var(--accent-true-blue)" }}
              />
              <span className="modal-title">WHAT TO BRING</span>
              <WorkshopLabelPill workshopId={workshopId} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  fontSize: "var(--fs-micro)",
                  color: "var(--text-muted)",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  marginRight: 12,
                }}
              >
                {requiredCount} required · {optionalCount} optional
              </span>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="modal-close-btn"
                aria-label="Close"
              >
                <X aria-hidden style={{ width: 12, height: 12 }} />
                CLOSE
              </button>
            </div>
          </div>

          {/* BODY */}
          <div className="modal-body">
            <div className="dt-pane dt-pane-active">
              <div className="flex flex-col gap-4">
                {bring.categories.map((cat) => {
                  const isRequired = cat.tag === "REQUIRED";
                  return (
                    <section key={cat.label} className="flex flex-col gap-2">
                      <header className="flex items-center gap-2">
                        <h3 className="text-foreground text-xs font-semibold tracking-[0.18em] uppercase">
                          {cat.label}
                        </h3>
                        <span
                          className={cn(
                            "rounded-full border px-2 py-0.5 text-fs-label font-semibold tracking-wider uppercase",
                            isRequired
                              ? "text-true-blue"
                              : "text-muted-foreground"
                          )}
                          style={
                            isRequired
                              ? {
                                  backgroundColor:
                                    "hsl(var(--accent-true-blue-hsl) / 0.12)",
                                  borderColor:
                                    "hsl(var(--accent-true-blue-hsl) / 0.4)",
                                }
                              : undefined
                          }
                        >
                          {cat.tag}
                        </span>
                      </header>
                      <ul className="flex flex-col gap-2">
                        {cat.items.map((item) => {
                          const key = itemKey(cat.label, item);
                          const isChecked = checked[key] === true;
                          const Icon = isChecked ? CheckSquare : Square;
                          return (
                            <li key={key}>
                              <button
                                type="button"
                                onClick={() =>
                                  setChecked((prev) => ({
                                    ...prev,
                                    [key]: !prev[key],
                                  }))
                                }
                                aria-pressed={isChecked}
                                className={cn(
                                  "border-border/40 bg-card/60 hover:bg-card/80 focus-visible:ring-ring flex w-full items-start gap-3 rounded-md border p-3 text-left transition focus-visible:ring-2 focus-visible:outline-none",
                                  isChecked && "bg-secondary/40"
                                )}
                              >
                                <Icon
                                  className={cn(
                                    "h-4 w-4 flex-shrink-0",
                                    isChecked
                                      ? "text-true-blue"
                                      : "text-muted-foreground"
                                  )}
                                  strokeWidth={1.8}
                                  aria-hidden
                                />
                                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                  <span
                                    className={cn(
                                      "text-foreground text-sm font-semibold",
                                      isChecked &&
                                        "text-muted-foreground line-through"
                                    )}
                                  >
                                    {item.label}
                                  </span>
                                  <span className="text-muted-foreground text-xs leading-snug">
                                    {item.description}
                                  </span>
                                </div>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  );
                })}

                <p className="text-muted-foreground border-border/40 border-t pt-3 text-xs leading-relaxed">
                  {bring.note}
                </p>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">
              Workshop Brief — What to Bring
            </span>
            <span className="modal-footer-sep">|</span>
            <span
              className="modal-footer-ws"
              style={{ color: workshopLabelColor(workshopId) }}
            >
              {workshopId} — Human-Agent Ways of Working
            </span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}
